// Locale configuration — single source of truth for supported languages,
// their URL prefixes and display names.

export const LOCALES = ["en-GB", "zh-HK", "ja-JP"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en-GB";

/** URL prefix per locale. English is unprefixed so existing URLs never change. */
export const LOCALE_PREFIX: Record<Locale, string> = {
  "en-GB": "",
  "zh-HK": "/zh-hk",
  "ja-JP": "/ja-jp",
};

/** Native display name shown in the language selector. */
export const LOCALE_LABEL: Record<Locale, string> = {
  "en-GB": "English",
  "zh-HK": "繁體中文",
  "ja-JP": "日本語",
};

/** Open Graph locale codes. */
export const OG_LOCALE: Record<Locale, string> = {
  "en-GB": "en_GB",
  "zh-HK": "zh_HK",
  "ja-JP": "ja_JP",
};

/** hreflang codes (identical to the locale tags). */
export const SITE_LOCALE_HREFLANG: Record<Locale, string> = {
  "en-GB": "en-GB",
  "zh-HK": "zh-HK",
  "ja-JP": "ja-JP",
};

export const LOCALE_COOKIE = "bn_locale";

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/** Normalises anything ("zh-hk", "ZH_HK", "ja") to a supported locale, or null. */
export function normaliseLocale(value: string | null | undefined): Locale | null {
  if (!value) return null;
  const v = value.replace("_", "-").toLowerCase();
  for (const l of LOCALES) {
    if (l.toLowerCase() === v) return l;
  }
  if (v.startsWith("zh")) {
    // Only Traditional (HK/TW/Hant) maps to zh-HK; Simplified falls back to English.
    if (v.includes("hant") || v.includes("hk") || v.includes("tw") || v.includes("mo")) return "zh-HK";
    return null;
  }
  if (v.startsWith("ja")) return "ja-JP";
  if (v.startsWith("en")) return "en-GB";
  return null;
}

/** Reads a locale from a pathname prefix, e.g. "/zh-hk/auctions" -> "zh-HK". */
export function localeFromPathname(pathname: string): Locale | null {
  const seg = pathname.toLowerCase().split("/")[1];
  if (!seg) return null;
  for (const l of LOCALES) {
    const prefix = LOCALE_PREFIX[l];
    if (prefix && prefix.slice(1) === seg) return l;
  }
  return null;
}

/** Strips any locale prefix from a pathname. */
export function stripLocalePrefix(pathname: string): string {
  const loc = localeFromPathname(pathname);
  if (!loc) return pathname || "/";
  const rest = pathname.slice(LOCALE_PREFIX[loc].length);
  return rest === "" ? "/" : rest;
}

/** Builds the absolute-path URL of `pathname` (unprefixed) in `locale`. */
export function localisePath(pathname: string, locale: Locale): string {
  const base = stripLocalePrefix(pathname);
  const prefix = LOCALE_PREFIX[locale];
  if (!prefix) return base;
  return base === "/" ? `${prefix}/` : `${prefix}${base}`;
}

/** Picks the best supported locale from an Accept-Language header value. */
export function localeFromAcceptLanguage(header: string | null | undefined): Locale | null {
  if (!header) return null;
  const parts = header
    .split(",")
    .map((p) => {
      const [tag, q] = p.trim().split(";q=");
      return { tag: tag.trim(), q: q ? Number(q) : 1 };
    })
    .sort((a, b) => b.q - a.q);
  for (const p of parts) {
    const l = normaliseLocale(p.tag);
    if (l) return l;
  }
  return null;
}
